import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Hide component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconHide extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2.3858 1.32416C2.09291 1.03127 1.61803 1.03127 1.32514 1.32416C1.03225 1.61706 1.03225 2.09193 1.32514 2.38482L9.26288 10.3225L13.6774 14.7371L21.6151 22.6748C21.908 22.9677 22.3829 22.9677 22.6758 22.6748C22.9687 22.3819 22.9687 21.9071 22.6758 21.6142L18.4315 17.3699C20.1338 16.1201 21.481 14.6476 22.3023 13.6532L22.3024 13.6533L22.3091 13.6448C22.6803 13.182 22.8761 12.5951 22.8761 11.9991C22.8761 11.4032 22.6803 10.8163 22.3091 10.3534L22.3092 10.3534L22.3023 10.3451C21.4615 9.32706 20.073 7.81241 18.3196 6.54693C16.5723 5.28585 14.3978 4.22559 12.0005 4.22559C10.1076 4.22559 8.35465 4.88681 6.84419 5.78255L2.3858 1.32416ZM7.94322 6.88158L9.84452 8.78289C11.3496 7.77172 13.4076 7.93138 14.7381 9.26187C16.0686 10.5924 16.2282 12.6503 15.217 14.1554L17.3572 16.2956C19.0021 15.1255 20.3279 13.6876 21.1419 12.7027C21.2865 12.5206 21.3761 12.2707 21.3761 11.9991C21.3761 11.7276 21.2865 11.4777 21.1419 11.2956C20.3436 10.3296 19.0492 8.92336 17.4417 7.76323C15.8256 6.5968 13.9577 5.72559 12.0005 5.72559C10.5819 5.72559 9.20946 6.18301 7.94322 6.88158ZM14.1221 13.0605C14.5665 12.1723 14.4182 11.0633 13.6774 10.3225C13.0254 9.67057 12.0883 9.47756 11.2675 9.74351C11.1557 9.77975 11.046 9.82451 10.9394 9.8778L14.1221 13.0605ZM4.96898 8.04509C5.24983 8.34955 5.23068 8.82404 4.92622 9.10489C4.06702 9.89742 3.36251 10.6863 2.85901 11.2956C2.71434 11.4777 2.62476 11.7276 2.62476 11.9991C2.62476 12.2707 2.71434 12.5205 2.859 12.7027C3.6573 13.6687 4.95172 15.0749 6.55919 16.235C8.17534 17.4015 10.0432 18.2727 12.0005 18.2727C12.7227 18.2727 13.4329 18.1544 14.1245 17.9452C14.521 17.8253 14.9396 18.0495 15.0595 18.4459C15.1794 18.8424 14.9552 19.261 14.5587 19.3809C13.7446 19.6272 12.8878 19.7727 12.0005 19.7727C9.60318 19.7727 7.42865 18.7124 5.68135 17.4513C3.92796 16.1859 2.53944 14.6712 1.69861 13.6532L1.6918 13.645L1.69186 13.6449C1.32053 13.182 1.12476 12.5951 1.12476 11.9991C1.12476 11.4032 1.32053 10.8162 1.69186 10.3534L1.69855 10.345L1.69861 10.3451C2.2333 9.69771 2.98569 8.85417 3.90919 8.00232C4.21365 7.72147 4.68814 7.74062 4.96898 8.04509Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-hide') === undefined) {
  customElements.define('cat-icon-hide', BlocksIconHide);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-hide': BlocksIconHide;
  }
}
